import SendInviteIcon from '@mui/icons-material/Email';
import { useCallback, useState } from 'react';
import { Confirm, useNotify, useTranslate } from 'react-admin';

import { PostB2bAdminsInvitesEndpoint } from '@Common/types/bo/b2b/admins';

import { Button } from '@sh4res/bo-common/components';
import { useCreatePromise, useResourceBasePath } from '@sh4res/bo-common/hooks';

interface B2bAdminSendInvitesButtonProps {
  adminIds: (number | string)[];
  companyId: number;
  disabled?: boolean;
  onSuccess?: () => void;
  onError?: () => void;
  testId: string;
}

export const B2bAdminSendInvitesButton = ({
  adminIds,
  companyId,
  disabled,
  onSuccess,
  onError,
  testId,
}: B2bAdminSendInvitesButtonProps) => {
  const [modalOpen, setModalOpen] = useState(false);
  const notify = useNotify();
  const resourceBasePath = useResourceBasePath();
  const [actionSendInvite, { isLoading }] = useCreatePromise<
    PostB2bAdminsInvitesEndpoint['Client']['RequestBody'],
    PostB2bAdminsInvitesEndpoint['Client']['ResponseBody']
  >();
  const translate = useTranslate();

  const handleSendInvite = useCallback(async () => {
    try {
      await actionSendInvite(`${resourceBasePath}admins/invite`, {
        data: {
          admin_ids: adminIds,
          b2b_company_id: companyId,
        },
      });
      notify('resources.bo_common.b2b.admins.actions.send_invite.dialog.success', {
        type: 'success',
      });
      onSuccess?.();
    } catch (error) {
      notify('resources.bo_common.b2b.admins.actions.send_invite.dialog.failure', {
        type: 'warning',
        messageArgs: { error },
        multiLine: true,
      });
      onError?.();
    } finally {
      setModalOpen(false);
    }
  }, [adminIds, companyId, actionSendInvite, notify, resourceBasePath, onSuccess, onError]);

  return (
    <>
      <Button
        permissions={{ resource: `${resourceBasePath}admins/invite`, action: 'create' }}
        data-testid={testId}
        label={translate('resources.bo_common.b2b.admins.actions.send_invite.label', {
          smart_count: adminIds.length,
        })}
        sx={{
          height: '30px',
        }}
        size="small"
        onClick={() => setModalOpen(true)}
        disabled={isLoading || disabled}>
        <SendInviteIcon />
      </Button>
      <Confirm
        isOpen={modalOpen}
        title="resources.bo_common.b2b.admins.actions.send_invite.dialog.title"
        content="resources.bo_common.b2b.admins.actions.send_invite.dialog.confirm"
        onConfirm={handleSendInvite}
        onClose={() => setModalOpen(false)}
        translateOptions={{ smart_count: adminIds.length }}
      />
    </>
  );
};
